import { publicIdSchema } from "@rosc/contracts";
import {
  appendChunk,
  putRecording,
  RecordingRemovedError,
  StorageFullError,
  updateRecording,
} from "./db";
import { CHUNK_INTERVAL_MS, HEARTBEAT_INTERVAL_MS } from "./limits";
import type { OfflineCaptureSettings, OfflineRecording } from "./types";

export const recordingLockName = (id: string): string =>
  `rosc-offline-recording:${id}`;

interface LockManagerLike {
  request: (
    name: string,
    callback: (lock: unknown) => Promise<void>,
  ) => Promise<void>;
  query?: () => Promise<{ held?: { name?: string }[] }>;
}

export const offlineRecordingLocks = (): LockManagerLike | null =>
  (globalThis.navigator as { locks?: LockManagerLike } | undefined)?.locks ??
  null;

/**
 * Ids of recordings a live tab holds, or null when the browser cannot tell
 * (then the heartbeat decides).
 */
export const heldRecordingIds = async (): Promise<Set<string> | null> => {
  const locks = offlineRecordingLocks();
  if (!locks?.query) return null;
  try {
    const { held = [] } = await locks.query();
    const prefix = recordingLockName("");
    return new Set(
      held
        .map((lock) => lock.name ?? "")
        .filter((name) => name.startsWith(prefix))
        .map((name) => name.slice(prefix.length)),
    );
  } catch {
    return null;
  }
};

/** The part of MediaRecorder this module uses (tests pass a fake). */
export interface MediaRecorderLike {
  readonly state: "inactive" | "recording" | "paused";
  readonly mimeType: string;
  ondataavailable: ((event: { data: Blob }) => void) | null;
  onstop: (() => void) | null;
  onerror: ((event: unknown) => void) | null;
  start: (timeslice?: number) => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  requestData?: () => void;
}

export type CreateRecorder = (
  stream: MediaStream,
  options: MediaRecorderOptions,
) => MediaRecorderLike;

const defaultCreateRecorder: CreateRecorder = (stream, options) =>
  new MediaRecorder(stream, options) as unknown as MediaRecorderLike;

export interface LocalCaptureOptions {
  uid: string;
  accountPublicId: string;
  capture: OfflineCaptureSettings;
  mimeType?: string;
  createRecorder?: CreateRecorder;
  now?: () => number;
  /** Called once when the device runs out of space (recording continues in memory). */
  onStorageError?: (error: Error) => void;
  /** Called after each saved chunk with the stored total. */
  onProgress?: (progress: { bytes: number; durationMs: number }) => void;
}

/**
 * Records a second, chunked copy of the microphone stream into IndexedDB.
 *
 * It runs beside the existing recorder (react-media-recorder keeps its own
 * in-memory blob and the realtime socket is untouched), so it can be added
 * without changing the transcription hooks. Writes are serialised; after a
 * storage failure it stops writing so the stored audio never has a gap.
 */
export class LocalRecordingCapture {
  readonly id: string;
  private readonly options: LocalCaptureOptions;
  private readonly now: () => number;
  private recorder: MediaRecorderLike | null = null;
  private seq = 0;
  private bytes = 0;
  private activeMs = 0;
  private runningSince: number | null = null;
  private writes: Promise<void> = Promise.resolve();
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private stopped: Promise<void> | null = null;
  private resolveStopped: (() => void) | null = null;
  private started: Promise<void> | null = null;
  private released = false;
  private unlock: (() => void) | null = null;
  storageFailed = false;

  constructor(options: LocalCaptureOptions) {
    this.options = {
      ...options,
      accountPublicId: publicIdSchema.parse(options.accountPublicId),
    };
    this.now = options.now ?? Date.now;
    this.id = crypto.randomUUID();
  }

  get durationMs(): number {
    return (
      this.activeMs +
      (this.runningSince === null ? 0 : this.now() - this.runningSince)
    );
  }

  get state(): "idle" | "recording" | "paused" | "stopped" {
    if (this.stopped) return "stopped";
    if (!this.recorder) return "idle";
    return this.recorder.state === "paused" ? "paused" : "recording";
  }

  /** Creates the row, then starts the chunked recorder. Idempotent. */
  start(stream: MediaStream): Promise<void> {
    this.started ??= this.begin(stream);
    return this.started;
  }

  private async begin(stream: MediaStream): Promise<void> {
    const now = this.now();
    const row: OfflineRecording = {
      id: this.id,
      version: 2,
      account: `${this.options.uid}:${this.options.accountPublicId}`,
      uid: this.options.uid,
      accountPublicId: this.options.accountPublicId,
      createdAt: now,
      updatedAt: now,
      heartbeatAt: now,
      status: "recording",
      mimeType: this.options.mimeType ?? "audio/webm",
      durationMs: 0,
      bytes: 0,
      chunkCount: 0,
      capture: this.options.capture,
    };
    try {
      await putRecording(row);
    } catch (error) {
      this.failStorage(error);
      return;
    }
    this.holdLock();
    if (this.stopped || this.released) return;
    const recorder = (this.options.createRecorder ?? defaultCreateRecorder)(
      stream,
      {
        ...(this.options.mimeType ? { mimeType: this.options.mimeType } : {}),
        audioBitsPerSecond: 24_000,
      },
    );
    this.recorder = recorder;
    recorder.ondataavailable = (event): void => {
      if (event.data && event.data.size > 0) this.save(event.data);
    };
    recorder.onstop = (): void => {
      this.pauseClock();
      this.resolveStopped?.();
    };
    recorder.onerror = (): void => {
      this.pauseClock();
      this.resolveStopped?.();
    };
    recorder.start(CHUNK_INTERVAL_MS);
    this.runningSince = this.now();
    const actualType = recorder.mimeType || row.mimeType;
    if (actualType !== row.mimeType)
      this.queueWrite(() =>
        updateRecording(this.id, (current) => ({
          ...current,
          mimeType: actualType,
        })).then(() => undefined),
      );
    this.heartbeat = setInterval(() => {
      void this.touch();
    }, HEARTBEAT_INTERVAL_MS);
  }

  pause(): void {
    if (this.recorder?.state !== "recording") return;
    this.recorder.pause();
    this.pauseClock();
  }

  resume(): void {
    if (this.recorder?.state !== "paused") return;
    this.recorder.resume();
    this.runningSince = this.now();
  }

  /**
   * Stops recording, waits for the final chunk and marks the row `stopped`.
   * The heartbeat keeps running until {@link release}.
   */
  stop(): Promise<void> {
    if (this.stopped) return this.stopped;
    this.stopped = (async () => {
      await this.started;
      const recorder = this.recorder;
      if (recorder && recorder.state !== "inactive") {
        const done = new Promise<void>((resolve) => {
          this.resolveStopped = resolve;
        });
        try {
          recorder.stop();
          await done;
        } catch {
          this.pauseClock();
        }
      }
      this.pauseClock();
      await this.writes;
      const durationMs = this.durationMs;
      try {
        await updateRecording(this.id, (current) =>
          current.status === "recording"
            ? {
                ...current,
                status: "stopped",
                durationMs: Math.max(current.durationMs, durationMs),
                updatedAt: this.now(),
                heartbeatAt: this.now(),
              }
            : null,
        );
      } catch {}
    })();
    return this.stopped;
  }

  /**
   * Gives up this tab's claim. A recording nobody submitted becomes
   * recoverable right away (Pending recordings, recovery on next load).
   */
  async release(): Promise<void> {
    if (this.released) return;
    this.released = true;
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
    await this.stop();
    this.unlock?.();
    this.unlock = null;
    try {
      await updateRecording(this.id, (current) =>
        current.status === "recording" || current.status === "stopped"
          ? { ...current, status: "stopped", heartbeatAt: 0 }
          : null,
      );
    } catch {}
  }

  /**
   * Hands over the audio recorded since the last slice now (the page is
   * being hidden or unloaded), so a reload or a closed tab loses as little
   * as possible. The chunk is written like any other.
   */
  saveNow(): void {
    if (this.recorder?.state !== "recording") return;
    try {
      this.recorder.requestData?.();
    } catch {}
  }

  /** Waits until every chunk received so far is stored. */
  flush(): Promise<void> {
    return this.writes;
  }

  /**
   * Holds a Web Lock named after the recording while this tab owns it, so
   * other tabs (and a reload) can tell a live recording from an abandoned one.
   */
  private holdLock(): void {
    const locks = offlineRecordingLocks();
    if (!locks || this.released) return;
    void locks
      .request(recordingLockName(this.id), () => {
        return new Promise<void>((resolve) => {
          this.unlock = resolve;
          if (this.released) resolve();
        });
      })
      .catch(() => undefined);
  }

  private pauseClock(): void {
    if (this.runningSince === null) return;
    this.activeMs += this.now() - this.runningSince;
    this.runningSince = null;
  }

  private async touch(): Promise<void> {
    try {
      await updateRecording(this.id, (current) => ({
        ...current,
        heartbeatAt: this.now(),
        durationMs: Math.max(current.durationMs, this.durationMs),
      }));
    } catch {}
  }

  private queueWrite(write: () => Promise<void>): void {
    this.writes = this.writes.then(write).catch((error: unknown) => {
      this.failStorage(error);
    });
  }

  private save(data: Blob): void {
    const seq = this.seq++;
    this.queueWrite(async () => {
      if (this.storageFailed) return;
      const buffer = await data.arrayBuffer();
      const durationMs = this.durationMs;
      await appendChunk(this.id, seq, buffer, {
        durationMs,
        now: this.now(),
      });
      this.bytes += buffer.byteLength;
      this.options.onProgress?.({ bytes: this.bytes, durationMs });
    });
  }

  private failStorage(error: unknown): void {
    if (this.storageFailed) return;
    this.storageFailed = true;
    // Discarded on purpose (Cancel, Discard): stop quietly.
    if (error instanceof RecordingRemovedError) return;
    const failure =
      error instanceof Error ? error : new StorageFullError(String(error));
    this.options.onStorageError?.(failure);
    void updateRecording(this.id, (current) => ({
      ...current,
      storageError: true,
    })).catch(() => undefined);
  }
}
